import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import { crewMembershipPath } from "@/routes"
import type { MasterclassCompletionRosterRow } from "@/types/masterclasses"

/**
 * OKS-57 — Single row in the per-masterclass Completions roster.
 *
 * Mirrors the OKS-26 `<CrewListRow>` avatar pattern: shadcn `Avatar`
 * size-9 with `AvatarFallback` initials (no avatar URL pipeline in V1
 * — TRD KD-9). Whole row is an Inertia `<Link>` to
 * `crewMembershipPath(membership_id)` per TRD KD-12 — the actionable
 * pivot from "this creator did/didn't complete" → "open their detail
 * to assign".
 *
 * Status indicator pairs colour with text (UX Spec §6 a11y):
 *  - Completed → emerald-500 filled dot + "Completed Mon DD, YYYY"
 *  - Not started → zinc-300 outlined dot + "Not started"
 *
 * The dot is `aria-hidden="true"` (decorative); SR announces the row
 * via the visible text status.
 */
export interface MasterclassCompletionsRosterRowProps {
  member: MasterclassCompletionRosterRow
}

export function MasterclassCompletionsRosterRow({
  member,
}: MasterclassCompletionsRosterRowProps) {
  const { t, i18n } = useTranslation()
  const getInitials = useInitials()

  // `display_name?.trim()` may yield `""` for whitespace-only names; we
  // want the handle fallback in that case too, so we keep the `||` short-
  // circuit on an explicit `length` check (nullish coalescing only handles
  // null/undefined, which would leak an empty initials block here).
  const trimmedName = member.display_name?.trim() ?? ""
  const initialsSource =
    trimmedName.length > 0 ? trimmedName : member.tiktok_handle
  const initials = getInitials(initialsSource)

  const completed = member.completed_at !== null

  const locale = i18n.language === "en" ? "en-US" : "fr-FR"
  const completedDate = member.completed_at
    ? new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(member.completed_at))
    : null

  const statusLine = completed
    ? t("masterclasses.show.completions.status_completed", {
        date: completedDate ?? "",
      })
    : t("masterclasses.show.completions.status_not_started")

  return (
    <li
      data-testid={`masterclass-completion-row-${member.membership_id}`}
      data-completed={completed ? "true" : "false"}
    >
      <Link
        href={crewMembershipPath(member.membership_id)}
        className="focus-visible:ring-ring/50 hover:bg-muted/50 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <Avatar className="size-9 shrink-0" aria-hidden="true">
          {/* avatar_url is always null V1 — initials fallback only. */}
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className="text-foreground truncate font-mono text-sm"
            data-testid={`masterclass-completion-row-${member.membership_id}-handle`}
          >
            @{member.tiktok_handle}
          </span>
          <span
            className="text-muted-foreground truncate text-xs"
            data-testid={`masterclass-completion-row-${member.membership_id}-status`}
          >
            {statusLine}
          </span>
        </div>
        <span
          aria-hidden="true"
          className={cn(
            "size-2.5 shrink-0 rounded-full",
            completed
              ? "bg-emerald-500"
              : "border-2 border-zinc-300 bg-transparent",
          )}
          data-testid={`masterclass-completion-row-${member.membership_id}-dot`}
          data-status={completed ? "completed" : "not-started"}
        />
      </Link>
    </li>
  )
}
